import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";

import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signOut,
  updateProfile
} from "firebase/auth";

import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc
} from "firebase/firestore";

import { auth, db } from "../firebase";
import { applyTheme, getUserPerks } from "../services/userPerksService";
import { performDailyCheckIn } from "../services/creditsService";
import { checkUsernameAvailable, reserveUsername } from "../services/usernameService";
import { reactivateUserAccount } from "../services/settingsService";

/** Apply default theme as early as possible */
applyTheme("warmCream");

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userPerks, setUserPerks] = useState(null);
  const [loading, setLoading] = useState(true);

  async function signup({ email, password, username, displayName }) {
    const cleanUsername = username.trim().toLowerCase().replace(/[^a-z0-9._]/g, "");
    const available = await checkUsernameAvailable(cleanUsername, null);
    if (!available) {
      throw new Error("Username is already taken or on hold.");
    }

    const result = await createUserWithEmailAndPassword(auth, email, password);

    await updateProfile(result.user, { displayName });
    await sendEmailVerification(result.user);

    await reserveUsername({ username: cleanUsername, uid: result.user.uid });

    const profileData = {
      uid: result.user.uid,
      email,
      username: cleanUsername,
      handle: cleanUsername,
      handleLocked: true,
      accountStatus: "active",
      displayName,
      bio: "",
      avatar: "",
      bannerImage: "",
      isCreator: false,
      role: "fan",
      approved: false,
      verified: false,
      followerCount: 0,
      supporterCount: 0,
      createdAt: serverTimestamp()
    };

    await setDoc(doc(db, "users", result.user.uid), profileData);
    setUserProfile(profileData);

    return result;
  }

  async function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  async function logout() {
    try {
      localStorage.removeItem("recentSearches");
      await signOut(auth);
      window.location.href = "/";
    } catch (error) {
      console.error("Logout error:", error);
    }
  }

  async function refreshUserProfile(uid) {
    try {
      const userDoc = await getDoc(doc(db, "users", uid));
      if (userDoc.exists()) {
        const profile = { uid: userDoc.id, ...userDoc.data() };
        setUserProfile(profile);
        return profile;
      }
      setUserProfile(null);
      return null;
    } catch (error) {
      console.error("Refresh user profile error:", error);
      setUserProfile(null);
      return null;
    }
  }

  async function refreshUserPerks(uid) {
    const targetUid = uid || auth.currentUser?.uid;
    if (!targetUid) return null;
    try {
      const perks = await getUserPerks(targetUid);
      setUserPerks(perks);
      const userDoc = await getDoc(doc(db, "users", targetUid));
      const savedTheme = userDoc.exists()
        ? userDoc.data().theme || perks?.theme
        : perks?.theme;
      applyTheme(savedTheme || "warmCream");
      return perks;
    } catch (error) {
      console.error("Refresh perks error:", error);
      return null;
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          let profileData = null;
          if (userDoc.exists()) {
            profileData = { uid: userDoc.id, ...userDoc.data() };
            // Deactivation is reversible on next login
            if (profileData.accountStatus === "deactivated" || profileData.profileHidden) {
              try {
                await reactivateUserAccount();
                const refreshed = await getDoc(doc(db, "users", user.uid));
                if (refreshed.exists()) {
                  profileData = { uid: refreshed.id, ...refreshed.data() };
                }
              } catch (reactivateErr) {
                console.warn("Account reactivate on login failed:", reactivateErr);
              }
            }
            setUserProfile(profileData);
          } else {
            setUserProfile(null);
          }

          const perks = await getUserPerks(user.uid);
          setUserPerks(perks);

          const savedTheme =
            profileData?.theme || perks?.theme || "warmCream";
          applyTheme(savedTheme);

          // Check-in mutates credits server-side — always re-read so Perks/Profile match
          performDailyCheckIn()
            .then(() => getUserPerks(user.uid))
            .then((fresh) => {
              if (fresh) setUserPerks(fresh);
            })
            .catch(console.warn);
        } catch (error) {
          console.error("User profile fetch error:", error);
          setUserProfile(null);
        }
      } else {
        setUserProfile(null);
        setUserPerks(null);
        applyTheme("warmCream");
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // ── MEMOIZE THE CONTEXT VALUE ──
  // Without this, `value` is a brand-new object on every render of
  // AuthProvider, which means every consumer of useAuth() (and every
  // useEffect that depends on currentUser/userProfile) re-fires on every
  // single re-render anywhere in the app — even unrelated ones. That was
  // causing save/like buttons to "re-check" Firestore mid-optimistic-update
  // and snap back to stale state. Memoizing keeps the reference stable
  // unless something inside it actually changed.
  const value = useMemo(() => ({
    currentUser,
    userProfile,
    userPerks,
    loading,
    signup,
    login,
    logout,
    refreshUserProfile,
    refreshUserPerks
  }), [currentUser, userProfile, userPerks, loading]);

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}